import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Button,
  Tooltip,
  InputAdornment,
  styled,
  Badge,
  Box,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { locationAPIService } from '../../../services/locationAPIService';

interface Location {
  type: 'country' | 'city';
  name: string;
  country?: string;
}

interface FilterBarProps {
  tempSearchQuery: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onApplyFilters: (filters: { fromLocation: Location | null; toLocation: Location | null; searchQuery: string }) => void;
  onReset: () => void;
}

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: -3,
    top: 3,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0px 5px',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

const StyledIconButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  width: 40,
  height: 40,
  padding: 0,
  borderRadius: 12,
  border: `1px solid ${theme.palette.grey[300]}`,
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}));

const SearchTextField = styled(TextField)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    minWidth: 220,
    maxWidth: 320,
  },
}));

const StyledAutocomplete = styled(Autocomplete<Location>)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    minWidth: 220,
    maxWidth: 320,
  },
}));

const FilterBarContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '16px',
  background: 'rgba(255, 255, 255, 0.5)',
  backdropFilter: 'blur(10px)',
  borderRadius: '10px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'center',
  },
}));

const FilterBar: React.FC<FilterBarProps> = ({
  tempSearchQuery,
  onSearchChange,
  onApplyFilters,
  onReset,
}) => {
  const theme = useTheme();
  const [fromSearch, setFromSearch] = useState('');
  const [toSearch, setToSearch] = useState('');
  const [fromValue, setFromValue] = useState<Location | null>(null);
  const [toValue, setToValue] = useState<Location | null>(null);
  const [fromSearchResults, setFromSearchResults] = useState<Location[]>([]);
  const [toSearchResults, setToSearchResults] = useState<Location[]>([]);
  const [defaultOptions, setDefaultOptions] = useState<Location[]>([]);

  const fetchDefaultLocations = async () => {
    try {
      // Fetch default countries
      const defaultCountries = [
        'United Kingdom',
        'Ghana',
        'Nigeria',
        'Kenya',
        'South Africa',
        'Uganda',
        'Tanzania',
        'Ethiopia'
      ];
      const countryOptions: Location[] = defaultCountries.map(name => ({ 
        type: 'country' as const, 
        name
      }));

      // Fetch popular cities from the API
      const res = await locationAPIService.searchLocations('london');
      const cityOptions: Location[] = res.cities
        .filter((city: any) => [
          'London',
          'Manchester',
          'Birmingham',
          'Liverpool',
          'Leeds',
          'Glasgow',
          'Edinburgh',
          'Bristol',
          'Cardiff',
          'Belfast'
        ].includes(city.name))
        .map((city: any) => ({
          type: 'city' as const,
          name: city.name,
          country: city.country
        }));

      setDefaultOptions([...countryOptions, ...cityOptions]);
    } catch (error) {
      console.error('Error fetching default locations:', error);
    }
  };

  useEffect(() => {
    fetchDefaultLocations();
  }, []);

  const searchLocations = async (query: string, setResults: (results: Location[]) => void) => {
    if (query.length < 3) return;
    try {
      const result = await locationAPIService.searchLocations(query);
      const locations: Location[] = [
        ...result.countries.map((country: string) => ({
          type: 'country' as const,
          name: country
        })),
        ...result.cities.map((city: { name: string; country: string }) => ({
          type: 'city' as const,
          name: city.name,
          country: city.country
        }))
      ];
      setResults(locations);
    } catch (error) {
      console.error('Error searching locations:', error);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchLocations(fromSearch, setFromSearchResults);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [fromSearch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchLocations(toSearch, setToSearchResults);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [toSearch]);

  // Calculate active filters count
  const activeFiltersCount = [
    fromValue?.name,
    toValue?.name,
    tempSearchQuery
  ].filter(Boolean).length;

  // Reset states when reset is called
  useEffect(() => {
    if (activeFiltersCount === 0) {
      setFromSearch('');
      setToSearch('');
      setFromValue(null);
      setToValue(null);
    }
  }, [activeFiltersCount]);

  const handleApply = () => {
    onApplyFilters({
      fromLocation: fromValue,
      toLocation: toValue,
      searchQuery: tempSearchQuery
    });
  };

  const handleReset = () => {
    setFromSearch('');
    setToSearch('');
    setFromValue(null);
    setToValue(null);
    setFromSearchResults([]);
    setToSearchResults([]);
    onReset();
  };

  return (
    <FilterBarContainer>
      <StyledAutocomplete
        options={fromSearchResults.length > 0 ? fromSearchResults : defaultOptions}
        groupBy={(option: Location) => option.type === 'country' ? 'Countries' : 'Cities'}
        getOptionLabel={(option: Location) => option.type === 'city' ? `${option.name}, ${option.country}` : option.name}
        value={fromValue}
        onChange={(_, value: Location | null) => setFromValue(value)}
        inputValue={fromSearch}
        onInputChange={(_, value) => setFromSearch(value)}
        noOptionsText={fromSearch.length < 3 ? "Type at least 3 characters to search" : "No locations found"}
        isOptionEqualToValue={(option: Location, value: Location) =>
          option.type === value.type &&
          option.name === value.name &&
          option.country === value.country
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Type to search more"
            variant="outlined"
            size="small"
            label="Shipping from"
            InputProps={{
              ...params.InputProps,
              style: {
                height: 40,
                background: 'rgba(255, 255, 255, 0.8)',
                borderRadius: 12,
                backdropFilter: 'blur(8px)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
              },
              endAdornment: params.InputProps.endAdornment,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.2s ease',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.12)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '1px',
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                color: 'text.secondary',
              },
            }}
          />
        )}
      />
      <StyledAutocomplete
        options={toSearchResults.length > 0 ? toSearchResults : defaultOptions}
        groupBy={(option: Location) => option.type === 'country' ? 'Countries' : 'Cities'}
        getOptionLabel={(option: Location) => option.type === 'city' ? `${option.name}, ${option.country}` : option.name}
        value={toValue}
        onChange={(_, value: Location | null) => setToValue(value)}
        inputValue={toSearch}
        onInputChange={(_, value) => setToSearch(value)}
        noOptionsText={toSearch.length < 3 ? "Type at least 3 characters to search" : "No locations found"}
        isOptionEqualToValue={(option: Location, value: Location) =>
          option.type === value.type &&
          option.name === value.name &&
          option.country === value.country
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Type to search more"
            variant="outlined"
            size="small"
            label="Shipping to"
            InputProps={{
              ...params.InputProps,
              style: {
                height: 40,
                background: 'rgba(255, 255, 255, 0.8)',
                borderRadius: 12,
                backdropFilter: 'blur(8px)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
              },
              endAdornment: params.InputProps.endAdornment,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.2s ease',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.12)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '1px',
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                color: 'text.secondary',
              },
            }}
          />
        )}
      />
      <SearchTextField
        size="medium"
        placeholder="Search shipping companies..."
        value={tempSearchQuery}
        onChange={onSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary', opacity: 0.7 }} />
            </InputAdornment>
          ),
          style: {
            height: 40,
            background: 'rgba(255, 255, 255, 0.8)',
            borderRadius: 12,
            backdropFilter: 'blur(8px)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
          },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.08)',
              transition: 'all 0.2s ease',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.12)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
              borderWidth: '1px',
            },
          },
        }}
      />
      <Box sx={{ 
        display: 'flex', 
        gap: 1,
        width: '100%',
        [theme.breakpoints.up('sm')]: {
          width: 'auto',
        }
      }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleApply}
          disabled={!fromValue?.name && !toValue?.name && !tempSearchQuery}
          sx={{
            flex: 1,
            height: 40,
            borderRadius: 12,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            boxShadow: '0 2px 4px rgba(33, 150, 243, 0.1)',
            transition: 'all 0.2s ease',
            [theme.breakpoints.up('sm')]: {
              flex: 'none',
              minWidth: 100,
            },
            '&.Mui-disabled': {
              background: 'rgba(255, 255, 255, 0.5)',
              color: 'text.disabled',
              borderColor: 'rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          Apply
        </Button>
        <Tooltip title="Reset filters">
          <span>
            <StyledBadge
              badgeContent={activeFiltersCount}
              invisible={activeFiltersCount === 0}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              overlap="circular"
            >
              <StyledIconButton
                onClick={handleReset}
                aria-label={`Reset all filters (${activeFiltersCount} active)`}
                sx={{
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(8px)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.9)',
                    transform: 'translateY(-1px)',
                  },
                  '&.Mui-disabled': {
                    background: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                <RestartAltIcon sx={{ 
                  color: activeFiltersCount > 0 ? 'primary.main' : 'text.secondary',
                  opacity: activeFiltersCount > 0 ? 1 : 0.7 
                }} />
              </StyledIconButton>
            </StyledBadge>
          </span>
        </Tooltip>
      </Box>
    </FilterBarContainer>
  );
};

export default FilterBar; 